'use client'

import { useDateTime, useLanguage } from '@repo/shared/hooks'
import { Col, Row, Typography } from '@repo/ui/components'

import {
  AreaSeries,
  LineType as LightweightLineType,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type Time,
  type WhitespaceData,
} from 'lightweight-charts'
import { useTheme } from 'next-themes'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './chart.css'
import { twMerge } from 'tailwind-merge'
import { PaymentType } from '@repo/shared/components'

export type LinePoint = { time: Time; value: number }

export type LineType = 'solid' | 'dashed'

export interface LineConfig {
  id: string
  color: string
  lineType?: LineType
  hasGradient?: boolean
  gradientTopColor?: string
  gradientBottomColor?: string
  lineWidth?: number
  visible?: boolean
  data?: LinePoint[]
  label?: string
  onlyValue?: boolean
}

export interface LineChartProps {
  lines?: LineConfig[]
  className?: string
  hidePrice?: boolean
  hideTime?: boolean
  showCustomTooltip?: boolean
  areaColor?: string
  formatPriceCompact?: boolean
  range?: string
}

export const withAlpha = (hexOrColor: string, alpha: number) => {
  const color = hexOrColor.trim()
  // If it's already rgba or similar, try to inject alpha if possible
  if (color.startsWith('rgba(')) return color
  if (color.startsWith('rgb(')) {
    const inner = color.slice(4, -1)
    return `rgba(${inner}, ${alpha})`
  }
  // Handle #RRGGBB or #RGB
  if (color.startsWith('#')) {
    let r = 0,
      g = 0,
      b = 0
    if (color.length === 7) {
      r = parseInt(color.slice(1, 3), 16)
      g = parseInt(color.slice(3, 5), 16)
      b = parseInt(color.slice(5, 7), 16)
    } else if (color.length === 4) {
      r = parseInt(color.charAt(1) + color.charAt(1), 16)
      g = parseInt(color.charAt(2) + color.charAt(2), 16)
      b = parseInt(color.charAt(3) + color.charAt(3), 16)
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  // Fallback
  return color
}

export const getCssVar = (name: string) => {
  if (typeof window === 'undefined') return ''
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    name,
  )
  return value?.trim()
}

export const formatCompactPrice = (
  price: number,
  language: 'fa' | 'en' = 'en',
): string => {
  const absPrice = Math.abs(price)
  let formatted: string

  if (absPrice >= 1_000_000_000) {
    formatted = (price / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  } else if (absPrice >= 1_000_000) {
    formatted = (price / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  } else if (absPrice >= 1_000) {
    formatted = (price / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  } else {
    formatted = price.toFixed(0)
  }

  if (language === 'fa') {
    // Convert English digits to Persian digits
    return formatted.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d)] || d)
  }

  return formatted
}

export const LineChart = ({
  lines,
  className,
  hidePrice = false,
  hideTime = false,
  showCustomTooltip = true,
  areaColor,
  formatPriceCompact = false,
  range = 'month',
}: LineChartProps) => {
  const { convertDateTimeToMonth } = useDateTime()
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRefs = useRef<Map<string, ISeriesApi<'Area'>>>(new Map())
  const { resolvedTheme } = useTheme()
  const language = useLanguage()
  const [inactiveFromIndex, setInactiveFromIndex] = useState<number | null>(
    null,
  )
  const [tooltipData, setTooltipData] = useState<{
    lines: Array<{
      id: string
      color: string
      value: number
      visible: boolean
      label?: string
    }>
    time: string
    visible: boolean
    x: number
    y: number
  } | null>(null)

  const getLineStyle = (lineType: LineType = 'solid') => {
    return lineType === 'dashed'
      ? LightweightLineType.Simple
      : LightweightLineType.Simple
  }

  // Determine time format based on range
  const timeFormat = useMemo(() => {
    if (!range) return 'day'
    if (range === '30d') return 'day'
    if (range === '90d') return 'month'
    if (range === '1y') return 'month'
    return 'month' // 30d, 90d
  }, [range])

  const formatTime = (time: Time) => {
    let timestamp: number
    if (typeof time === 'number') {
      timestamp = time * 1000
    } else if (typeof time === 'string') {
      timestamp = new Date(time).getTime()
    } else {
      // Handle BusinessDay type
      const businessDay = time as {
        year: number
        month: number
        day: number
      }
      timestamp = new Date(
        businessDay.year,
        businessDay.month - 1,
        businessDay.day,
      ).getTime()
    }

    switch (timeFormat) {
      case 'day': {
        const date = new Date(timestamp)
        return date.toLocaleDateString(language === 'fa' ? 'fa-IR' : 'en-US', {
          day: 'numeric',
        })
      }
      case 'month':
      default:
        return convertDateTimeToMonth(timestamp)
    }
  }

  const applyThemeToChart = useCallback(() => {
    const chart = chartRef.current
    if (!chart) return
    const bg = getCssVar('--color-neutral-surface-light')
    const text = getCssVar('--color-neutral-main')
    const grid = getCssVar('--color-neutral-surface-5')
    const axis = getCssVar('--color-neutral-surface-light')
    chart.applyOptions({
      layout: {
        background: { color: bg || undefined },
        textColor: text || undefined,
        fontFamily: getCssVar('--font-family') || undefined,
        fontSize: 10,
      },
      grid: {
        horzLines: { color: grid || undefined },
        vertLines: { color: grid || undefined },
      },
      leftPriceScale: {
        visible: !hidePrice || formatPriceCompact,
        borderColor: axis || undefined,
        borderVisible: true,
        alignLabels: true,
        autoScale: true,
      },
      timeScale: {
        visible: !hideTime,
        borderColor: axis || undefined,
        borderVisible: true,
        timeVisible: !hideTime,
        tickMarkFormatter: (time: Time) => formatTime(time),
      },
      localization: {
        timeFormatter: hideTime ? () => '' : (time: Time) => formatTime(time),
        priceFormatter:
          hidePrice && !formatPriceCompact
            ? () => ''
            : (price: number) => {
                if (formatPriceCompact) {
                  return formatCompactPrice(price, language as 'fa' | 'en')
                }
                // Add comma separator and convert digits based on language
                const formattedPrice = price.toLocaleString()

                if (language === 'fa') {
                  // Convert English digits to Persian digits
                  return formattedPrice.replace(
                    /[0-9]/g,
                    (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d)] || d,
                  )
                }
                return formattedPrice
              },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, formatPriceCompact, hidePrice])

  const updateChartData = useCallback(() => {
    const chart = chartRef.current
    if (!chart) return

    // Handle lines configuration
    if (lines && lines.length > 0) {
      lines.forEach((lineConfig) => {
        const series = seriesRefs.current.get(lineConfig.id)
        if (series && lineConfig.data) {
          if (lineConfig.visible !== false) {
            // Apply active/inactive logic if hover is active
            if (
              inactiveFromIndex !== null &&
              inactiveFromIndex < lineConfig.data.length
            ) {
              // Split data into active and inactive parts
              const activeData = lineConfig.data.slice(0, inactiveFromIndex + 1)
              const inactiveData = lineConfig.data.slice(inactiveFromIndex)

              // Set active data
              series.setData(
                activeData as unknown as (
                  | LineData<Time>
                  | WhitespaceData<Time>
                )[],
              )

              // Create inactive series if it doesn't exist
              const inactiveSeriesId = `${lineConfig.id}-inactive`
              let inactiveSeries = seriesRefs.current.get(inactiveSeriesId)

              if (!inactiveSeries) {
                const inactiveColor = getCssVar('--color-neutral-surface-20')
                inactiveSeries = chart.addSeries(AreaSeries, {
                  lineWidth: (lineConfig.lineWidth || 2) as any,
                  lastValueVisible: false,
                  priceLineVisible: false,
                  crosshairMarkerVisible: true,
                  priceScaleId: 'left',
                  lineColor: inactiveColor,
                  topColor: withAlpha(
                    inactiveColor,
                    lineConfig.hasGradient ? 0.24 : 0,
                  ),
                  bottomColor: withAlpha(inactiveColor, 0),
                  crosshairMarkerRadius: 2,
                  crosshairMarkerBorderColor: inactiveColor,
                  crosshairMarkerBackgroundColor: inactiveColor,
                  lineType: getLineStyle(lineConfig.lineType),
                })
                seriesRefs.current.set(inactiveSeriesId, inactiveSeries)
              }

              // Set inactive data
              inactiveSeries.setData(
                inactiveData as unknown as (
                  | LineData<Time>
                  | WhitespaceData<Time>
                )[],
              )
              inactiveSeries.applyOptions({
                visible: true,
              })
            } else {
              // No inactive state, show all data normally
              series.setData(
                lineConfig.data as unknown as (
                  | LineData<Time>
                  | WhitespaceData<Time>
                )[],
              )

              // Hide inactive series if it exists
              const inactiveSeriesId = `${lineConfig.id}-inactive`
              const inactiveSeries = seriesRefs.current.get(inactiveSeriesId)
              if (inactiveSeries) {
                inactiveSeries.setData([])
                inactiveSeries.applyOptions({
                  visible: false,
                })
              }
            }

            // Make series visible
            series.applyOptions({
              visible: true,
            })
          } else {
            // Hide the series by making invisible (keep data intact)
            series.applyOptions({
              visible: false,
            })
          }
        }
      })
      return
    }
  }, [lines, inactiveFromIndex])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const chart = createChart(container, {
      autoSize: true,
      handleScale: {
        axisPressedMouseMove: { time: false, price: false },
        mouseWheel: false,
        pinch: false,
      },
      handleScroll: {
        mouseWheel: false,
        pressedMouseMove: false,
        horzTouchDrag: false,
        vertTouchDrag: false,
      },
      layout: {
        background: {
          color: getCssVar('--color-neutral-surface-light') || undefined,
        },
        textColor: getCssVar('--color-neutral-main') || undefined,
        fontFamily: getCssVar('--font-family') || undefined,
        fontSize: 10,
      },
      grid: {
        horzLines: {
          color: getCssVar('--color-neutral-surface-5') || undefined,
        },
        vertLines: {
          color: getCssVar('--color-neutral-surface-5') || undefined,
        },
      },
      timeScale: {
        visible: !hideTime,
        timeVisible: !hideTime,
        borderColor: getCssVar('--color-neutral-surface-light') || undefined,
        borderVisible: true,
        minBarSpacing: 6,
        tickMarkFormatter: (time: Time) => `${formatTime(time)}`,
      },
      leftPriceScale: {
        visible: !hidePrice || formatPriceCompact,
        borderColor: getCssVar('--color-neutral-surface-light') || undefined,
        borderVisible: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        alignLabels: true,
        autoScale: true,
      },
      rightPriceScale: { visible: false },
      crosshair: {
        mode: showCustomTooltip ? 0 : 1,
        vertLine: {
          labelVisible: showCustomTooltip ? false : !hideTime,
          width: 1,
        },
        horzLine: {
          labelVisible: showCustomTooltip
            ? false
            : !hidePrice || formatPriceCompact,
          width: 1,
        },
      },
      localization: {
        timeFormatter: hideTime ? () => '' : (time: Time) => formatTime(time),
        priceFormatter:
          hidePrice && !formatPriceCompact
            ? () => ''
            : (price: number) => {
                if (formatPriceCompact) {
                  return formatCompactPrice(price, language as 'fa' | 'en')
                }
                // Add comma separator and convert digits based on language
                const formattedPrice = price.toLocaleString()

                if (language === 'fa') {
                  // Convert English digits to Persian digits
                  return formattedPrice.replace(
                    /[0-9]/g,
                    (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d)] || d,
                  )
                }
                return formattedPrice
              },
      },
    })

    chartRef.current = chart

    // Handle lines configuration
    if (lines && lines.length > 0) {
      lines.forEach((lineConfig) => {
        const topColor = lineConfig.hasGradient
          ? lineConfig.gradientTopColor || withAlpha(lineConfig.color, 0.24)
          : 'transparent'
        const bottomColor = lineConfig.hasGradient
          ? lineConfig.gradientBottomColor || withAlpha(lineConfig.color, 0)
          : 'transparent'

        const series = chart.addSeries(AreaSeries, {
          lineWidth: (lineConfig.lineWidth || 2) as any,
          lastValueVisible: false,
          priceLineVisible: false,
          crosshairMarkerVisible: true,
          priceScaleId: 'left',
          lineColor: lineConfig.color,
          topColor,
          bottomColor,
          crosshairMarkerRadius: 2,
          crosshairMarkerBorderColor: lineConfig.color,
          crosshairMarkerBackgroundColor: lineConfig.color,
          lineType: getLineStyle(lineConfig.lineType),
        })
        seriesRefs.current.set(lineConfig.id, series)

        // Set initial visibility and data
        if (lineConfig.data) {
          series.setData(
            lineConfig.data as unknown as (
              | LineData<Time>
              | WhitespaceData<Time>
            )[],
          )
        }

        series.applyOptions({
          visible: lineConfig.visible !== false,
        })
      })
    }

    // Add hover handler
    chart.subscribeCrosshairMove((param) => {
      if (param.time && param.seriesData.size > 0) {
        // Handle lines configuration
        if (lines && lines.length > 0) {
          // Find the closest data point to the hovered time for active/inactive logic
          const hoveredTime = param.time
          let closestIndex = 0
          let minDiff = Infinity
          let hasValidData = false

          // Check all lines to find the closest point
          lines.forEach((lineConfig) => {
            if (lineConfig.data && lineConfig.visible !== false) {
              lineConfig.data.forEach((point, index) => {
                let pointTime: number
                let hoveredTimeValue: number

                // Handle point.time
                if (typeof point.time === 'number') {
                  pointTime = point.time
                } else if (typeof point.time === 'string') {
                  pointTime = new Date(point.time).getTime() / 1000
                } else {
                  // Handle BusinessDay type
                  const businessDay = point.time as {
                    year: number
                    month: number
                    day: number
                  }
                  pointTime =
                    new Date(
                      businessDay.year,
                      businessDay.month - 1,
                      businessDay.day,
                    ).getTime() / 1000
                }

                // Handle hoveredTime
                if (typeof hoveredTime === 'number') {
                  hoveredTimeValue = hoveredTime
                } else if (typeof hoveredTime === 'string') {
                  hoveredTimeValue = new Date(hoveredTime).getTime() / 1000
                } else {
                  // Handle BusinessDay type
                  const businessDay = hoveredTime as {
                    year: number
                    month: number
                    day: number
                  }
                  hoveredTimeValue =
                    new Date(
                      businessDay.year,
                      businessDay.month - 1,
                      businessDay.day,
                    ).getTime() / 1000
                }

                const diff = Math.abs(pointTime - hoveredTimeValue)

                if (diff < minDiff) {
                  minDiff = diff
                  closestIndex = index
                  hasValidData = true
                }
              })
            }
          })

          // Set inactive state based on closest point
          if (
            hasValidData &&
            closestIndex > 0 &&
            lines[0]?.data &&
            closestIndex < lines[0].data.length - 1
          ) {
            setInactiveFromIndex(closestIndex)
          } else {
            setInactiveFromIndex(null)
          }

          // For lines, show tooltip with all visible series data
          if (showCustomTooltip) {
            const seriesData = Array.from(param.seriesData.entries())
            if (seriesData.length > 0) {
              const timePoint = param.time
              let timeValue: number

              if (typeof timePoint === 'number') {
                timeValue = timePoint
              } else if (typeof timePoint === 'string') {
                timeValue = new Date(timePoint).getTime() / 1000
              } else {
                const businessDay = timePoint as {
                  year: number
                  month: number
                  day: number
                }
                timeValue =
                  new Date(
                    businessDay.year,
                    businessDay.month - 1,
                    businessDay.day,
                  ).getTime() / 1000
              }

              const time =
                new Date(timeValue * 1000).toLocaleDateString(
                  language === 'fa' ? 'fa-IR' : 'en-US',
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  },
                ) +
                ' - ' +
                new Date(timeValue * 1000).toLocaleTimeString('en-US', {
                  hour12: false,
                  hour: '2-digit',
                  minute: '2-digit',
                })

              // Collect data from all visible series
              const tooltipLines = seriesData
                .map(([series, data]) => {
                  const seriesId =
                    Array.from(seriesRefs.current.entries()).find(
                      ([_, s]) => s === series,
                    )?.[0] || 'unknown'

                  // Skip inactive series in tooltip
                  if (seriesId.includes('-inactive')) return null

                  const lineConfig = lines.find((l) => l.id === seriesId)
                  const value = (data as any).value || 0

                  return {
                    id: seriesId,
                    color: lineConfig?.color || '#000',
                    value: Number(value),
                    visible: Boolean(lineConfig?.visible),
                    label: lineConfig?.label || '',
                  }
                })
                .filter(
                  (line): line is NonNullable<typeof line> =>
                    line !== null && line.visible,
                )

              // Get mouse position for tooltip placement
              const mouseX = param.point?.x || 0
              const mouseY = param.point?.y || 0

              setTooltipData({
                lines: tooltipLines,
                time,
                visible: true,
                x: mouseX,
                y: mouseY - 80, // Position above the point
              })
            }
          }
        }
      } else {
        // Reset to normal state when mouse leaves the chart
        setInactiveFromIndex(null)
        if (showCustomTooltip) {
          setTooltipData(null)
        }
      }
    })

    // initial data (guard for empty to avoid unnecessary work during hydration)
    if (lines && lines.length > 0) {
      updateChartData()
      chart.timeScale().fitContent()
      // Auto-scale price axis to show min to max range
      chart.priceScale('left').applyOptions({ autoScale: true })
    }

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ autoSize: true })
    })
    ro.observe(container)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
      seriesRefs.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, language, formatPriceCompact, hidePrice])

  // react to theme changes by re-applying colors
  useEffect(() => {
    // Defer to next animation frame so that CSS variables reflect the updated theme
    const raf = requestAnimationFrame(() => {
      applyThemeToChart()
    })
    return () => cancelAnimationFrame(raf)
  }, [resolvedTheme, applyThemeToChart])

  // update on lines change or inactive state change
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    updateChartData()
    chart.timeScale().fitContent()
    // Auto-scale price axis to show min to max range
    chart.priceScale('left').applyOptions({ autoScale: true })
  }, [lines, inactiveFromIndex, updateChartData])

  // update colors when theme changes with delay to ensure CSS variables are updated
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !lines || lines.length === 0) return

    // Use requestAnimationFrame to ensure DOM is updated
    const raf = requestAnimationFrame(() => {
      // Add a small delay to ensure CSS variables are updated
      setTimeout(() => {
        lines.forEach((lineConfig) => {
          const series = seriesRefs.current.get(lineConfig.id)
          if (series) {
            // Get fresh color from CSS variable
            const lineColor =
              lineConfig.color || getCssVar('--color-base-main') || '#3B82F6'
            const topColor = lineConfig.hasGradient
              ? lineConfig.gradientTopColor || withAlpha(lineColor, 0.24)
              : 'transparent'
            const bottomColor = lineConfig.hasGradient
              ? lineConfig.gradientBottomColor || withAlpha(lineColor, 0)
              : 'transparent'

            series.applyOptions({
              lineColor,
              topColor,
              bottomColor,
              crosshairMarkerBorderColor: lineColor,
              crosshairMarkerBackgroundColor: lineColor,
            })
          }

          // Also update inactive series if it exists
          const inactiveSeriesId = `${lineConfig.id}-inactive`
          const inactiveSeries = seriesRefs.current.get(inactiveSeriesId)
          if (inactiveSeries) {
            const inactiveColor = getCssVar('--color-neutral-surface-20')
            const inactiveTopColor = lineConfig.hasGradient
              ? withAlpha(inactiveColor, 0.24)
              : 'transparent'
            const inactiveBottomColor = lineConfig.hasGradient
              ? withAlpha(inactiveColor, 0)
              : 'transparent'

            inactiveSeries.applyOptions({
              lineColor: inactiveColor,
              topColor: inactiveTopColor,
              bottomColor: inactiveBottomColor,
              crosshairMarkerBorderColor: inactiveColor,
              crosshairMarkerBackgroundColor: inactiveColor,
            })
          }
        })
      }, 50) // Small delay to ensure CSS variables are updated
    })

    return () => cancelAnimationFrame(raf)
  }, [resolvedTheme, lines])

  return (
    <div className={twMerge('relative', className)}>
      <div ref={containerRef} className={className} />
      {showCustomTooltip && tooltipData && (
        <Col
          className='rtl-force ltr-force justify-center items-center w-fit absolute pointer-events-none !z-10 bg-neutral-surface/20 backdrop-blur-sm rounded-xs border border-neutral-surface-5 shadow-lg px-sm py-xs text-neutral-surface-100'
          style={{
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`,
            transform: 'translateX(-50%)',
          }}
        >
          {tooltipData.lines.map((line) => {
            const lineConfig = lines?.find((l) => l.id === line.id)
            if (!lineConfig?.visible) return null
            return (
              <Col key={line.id} className='items-start mb-sm'>
                {line?.label && (
                  <Row className='items-center gap-xs'>
                    <span
                      className='inline-block w-3 h-1 rounded'
                      style={{ backgroundColor: line.color }}
                    />
                    {
                      <Typography
                        size='xs'
                        className='opacity-90 whitespace-nowrap'
                      >
                        {line?.label}
                      </Typography>
                    }
                  </Row>
                )}
                <bdi className='w-full rtl:text-left ltr:text-right'>
                  {lineConfig?.onlyValue ? (
                    <Typography
                      size='sm'
                      className='whitespace-nowrap'
                      weight='bold'
                    >
                      {line?.value?.toLocaleString()}
                    </Typography>
                  ) : (
                    <PaymentType value={line.value * 10} size='xs' />
                  )}
                </bdi>
              </Col>
            )
          })}
          <Typography size='xs' className='opacity-90 whitespace-nowrap ltr'>
            {tooltipData.time}
          </Typography>
        </Col>
      )}
    </div>
  )
}
